import { useNavigate } from 'react-router-dom';
import type { PartnerShopListItem } from '@/types/partnerShop';

interface PartnerShopCardProps {
  shop: PartnerShopListItem;
}

export default function PartnerShopCard({ shop }: PartnerShopCardProps) {
  const navigate = useNavigate();
  const totalPhotos = shop.reviewPhotoCount + shop.lookbookPhotoCount;
  const detailUrl = `/detail/${shop.shopSlug}`
  console.log(detailUrl)
  return (
    <article
      onClick={() => navigate(detailUrl)}
      className="bg-white rounded-2xl overflow-hidden cursor-pointer group transition-all duration-200 hover:border-[#B9965B] border border-transparent"
    >
      {/* Image */}
      <div className="relative aspect-[4/3] lg:aspect-[16/10] bg-[#F7F2EA] overflow-hidden">
        {shop.heroThumbnailUrl ? (
          <img
            src={shop.heroThumbnailUrl}
            alt={shop.displayName}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
            loading="lazy"
          />
        ) : shop.heroImageUrl ? (
          <img
            src={shop.heroImageUrl}
            alt={shop.displayName}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#F7F2EA] to-[#E8E2DA] flex items-center justify-center">
            <i className="ri-image-line text-3xl text-[#B0B8C1]" />
          </div>
        )}
        <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5 bg-[#121B35] text-white text-[10px] lg:text-[11px] px-2 py-0.5 rounded-full font-medium">
          <span className="w-1 h-1 rounded-full bg-[#B9965B]" />
          다이렉트 인증
        </div>
        <span className="absolute top-2.5 right-2.5 bg-white/90 backdrop-blur-sm text-[#121B35] text-[10px] lg:text-[11px] px-2 py-0.5 rounded-full font-medium">
          {shop.areaName}
        </span>
        {totalPhotos > 0 && (
          <div className="absolute bottom-2.5 left-2.5 bg-black/50 text-white text-[10px] px-1.5 py-0.5 rounded-full">
            <i className="ri-image-line mr-0.5" />사진 {totalPhotos}장
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3 lg:p-4">
        <h3 className="text-[14px] lg:text-base font-bold text-[#121B35] leading-tight">
          {shop.displayName}
        </h3>
        <p className="text-[11px] lg:text-xs text-[#536079] mt-0.5">
          {shop.areaName}{shop.branchName ? ` · ${shop.branchName}` : ''} · {shop.shopCategory}
        </p>

        {/* Short description — desktop only */}
        {shop.shortDescription && (
          <p className="hidden lg:block text-[13px] text-[#536079] mt-2 leading-relaxed line-clamp-1">
            {shop.shortDescription}
          </p>
        )}

        {/* Review summary */}
        <div className="mt-2.5 lg:mt-3 bg-[#F7F2EA] rounded-xl px-2.5 py-2 lg:px-3 lg:py-2.5">
          <span className="text-[10px] lg:text-[11px] text-[#B9965B] font-medium uppercase tracking-wide">후기 요약</span>
          <p className="text-[11px] lg:text-[13px] text-[#121B35] leading-relaxed line-clamp-2 mt-0.5 italic">
            &ldquo;{shop.reviewSummaryText || '상담 전 확인할 수 있는 업체 정보와 사진을 준비 중입니다.'}&rdquo;
          </p>
        </div>

        {/* Meta info */}
        <div className="flex flex-wrap items-center gap-x-2 lg:gap-x-3 gap-y-1 mt-2.5 lg:mt-3">
          <div className="flex items-center gap-1 text-[10px] lg:text-xs text-[#536079]">
            <i className="ri-message-3-line text-[#B9965B]" />
            <span>후기 {shop.reviewCount}건</span>
          </div>
          {/* Direct review — desktop only */}
          <div className="hidden lg:flex items-center gap-1 text-[11px] text-[#536079]">
            <i className="ri-star-line text-[#B9965B]" />
            <span>직접 후기 {shop.directReviewCount}건</span>
          </div>
          <div className="flex items-center gap-1 text-[10px] lg:text-xs text-[#536079]">
            <i className="ri-camera-line text-[#B9965B]" />
            <span>사진 {totalPhotos}장</span>
          </div>
          {shop.priceText && (
            <span className="text-[10px] lg:text-xs text-[#536079]">
              · 예상 구성가 {shop.priceText}
            </span>
          )}
        </div>

        {/* Keywords — mobile 2, desktop 3 */}
        {shop.keywords.length > 0 && (
          <div className="flex flex-wrap gap-1 lg:gap-1.5 mt-2 lg:mt-3">
            {shop.keywords.slice(0, 3).map((kw, i) => (
              <span
                key={kw}
                className={`text-[10px] lg:text-[11px] text-[#536079] bg-[#F7F2EA] px-2 py-0.5 rounded-full ${i >= 2 ? 'hidden lg:inline' : ''}`}
              >
                #{kw}
              </span>
            ))}
          </div>
        )}

        {/* Promotion — desktop only */}
        {shop.hasPromotion && (
          <div className="hidden lg:flex mt-3 items-center gap-1.5 text-[12px] text-[#B9965B] font-medium">
            <i className="ri-gift-line" />
            <span>{shop.promotionText || '계약 혜택 있음'}</span>
          </div>
        )}

        {/* CTA buttons */}
        <div className="flex items-center gap-1.5 lg:gap-2 mt-3 pt-2.5 lg:pt-3 border-t border-[#F7F2EA]">
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`${detailUrl}`); }}
            className="flex-1 lg:flex-none text-center text-[11px] lg:text-[13px] font-medium text-white bg-[#121B35] hover:bg-[#1A2342] rounded-lg py-2 lg:px-5 transition-colors whitespace-nowrap"
          >
            상세보기
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`${detailUrl}/reviews`); }}
            className="text-[11px] lg:text-[13px] text-[#536079] hover:text-[#121B35] px-1.5 lg:px-2 py-2 transition-colors whitespace-nowrap"
          >
            <span className="lg:hidden">후기</span>
            <span className="hidden lg:inline">후기 보기</span>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`${detailUrl}/lookbook`); }}
            className="text-[11px] lg:text-[13px] text-[#536079] hover:text-[#121B35] px-1.5 lg:px-2 py-2 transition-colors whitespace-nowrap"
          >
            <span className="lg:hidden">룩북</span>
            <span className="hidden lg:inline">룩북 보기</span>
          </button>
        </div>
      </div>
    </article>
  );
}